import { useApi } from '@src/app/context/Api';
import { TagSelectionField } from '@src/entities/Tag/ui/components/fields/TagSelection';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { isEmpty, isEqual, isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';

/**
 * AssignTags
 *
 * Displays a modal to assign action paths to the risk record.
 *
 * @param eid
 * @param handleSearch
 * @param isOpen
 * @param recordTags
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function AssignTags({
  eid = '',
  handleSearch = () => {},
  isOpen = false,
  recordTags = [],
  setIsOpen = () => {},
}) {
  const [currentTags, setCurrentTags] = useState([]);
  const [addedTags, setAddedTags] = useState([]);
  const [removedTags, setRemovedTags] = useState([]);

  const { clearApiCache, useApiQuery } = useApi('Tag');
  const { useApiMutation } = useApi('RiskRecord');

  const [ListTags, { data: tags, isFetching, isLoading }] = useApiQuery('ListTags');
  const [RiskRecordTagAssignment] = useApiMutation('RiskRecordTagAssignment', {
    excludedHttpCodes: [400],
  });
  const [RiskRecordTagUnassignment] = useApiMutation('RiskRecordTagUnassignment', {
    excludedHttpCodes: [400],
  });

  const defaultValues = {
    tags: [],
  };

  const form = useForm({ defaultValues });
  const { toast } = useToast();

  /**
   * Retrieve the list of user defined tags
   */
  useEffect(() => {
    if (isUndefined(tags)) {
      ListTags({
        page: 0,
        size: 9999,
        sort: 'code,asc',
        userDefined: true,
      });
    }
  }, [tags]);

  useEffect(() => {
    if (!isUndefined(tags)) {
      const theTags = tags?.content
        ?.filter((tag) => {
          return recordTags?.find((recordTag) => {
            return recordTag === tag?.tag;
          });
        })
        .map((tag) => {
          return tag.tag;
        });

      setCurrentTags(theTags);

      form?.reset(
        {
          tags: theTags,
        },
        {
          keepDirty: false,
          keepDirtyValues: false,
          keepErrors: false,
          keepIsValid: false,
          keepSubmitCount: true,
          keepTouched: false,
          keepValues: false,
        }
      );
    }
  }, [tags]);

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @returns {Promise<boolean>}
   */
  const handleSubmit = async () => {
    if (!form?.formState?.isDirty) {
      return false;
    }

    if (!isEmpty(removedTags)) {
      const toastId = `tag-unassignment-${eid}`;
      toast.show({
        ariaLoadingLabel: 'Unassigning Tags from EID',
        autoClose: false,
        id: `${toastId}-info`,
        isLoading: true,
        message: 'Unassigning Tags from EID...',
        title: 'Unassigning Tags from EID',
        variant: 'info',
      });

      const unassignments = removedTags?.map(async (tag) => {
        const payload = {
          eid,
          tagCode: tag,
        };

        // eslint-disable-next-line @typescript-eslint/return-await
        return await RiskRecordTagUnassignment(payload, {
          onError: () => {
            toast.hide(`${toastId}-info`);
            toast.show({
              id: `${toastId}-error`,
              message: 'Unable from unassign Tags.',
              title: 'Unassigning Tags Failed',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.hide(`${toastId}-info`);
            toast.show({
              id: `${toastId}-success`,
              message: 'Tags have been successfully unassigned.',
              title: 'Unassigned Tags from EID',
              variant: 'success',
            });
          },
        });
      });
      await Promise.all(unassignments);
    }

    if (!isEmpty(addedTags)) {
      const toastId = `tag-assignment-${eid}`;
      toast.show({
        ariaLoadingLabel: 'Assigning Tags to EID',
        autoClose: false,
        id: `${toastId}-info`,
        isLoading: true,
        message: 'Assigning Tags to EID...',
        title: 'Assigning Tags to EID',
        variant: 'info',
      });

      const assignments = addedTags?.map(async (tag) => {
        const payload = {
          eid,
          tagCode: tag,
        };

        // eslint-disable-next-line @typescript-eslint/return-await
        return await RiskRecordTagAssignment(payload, {
          onError: () => {
            toast.hide(`${toastId}-info`);
            toast.show({
              id: `${toastId}-error`,
              message: 'Unable to assign Tags.',
              title: 'Assigning Tags Failed',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.hide(`${toastId}-info`);
            toast.show({
              id: `${toastId}-success`,
              message: 'Tags have been successfully assigned.',
              title: 'Assigned Tags to EID',
              variant: 'success',
            });
          },
        });
      });

      await Promise.all(assignments);
    }

    setIsOpen(false);
    await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['GetRiskRecord'] }]);
    handleSearch();

    return true;
  };

  /**
   * handleChange
   *
   * Handles the change event for the tags.
   *
   * @param values
   */
  const handleChange = (values) => {
    const newAddedTags = values.filter((item) => {
      return !currentTags?.includes(item);
    });
    const newRemovedTags = currentTags?.filter((item) => {
      return !values.includes(item);
    });

    if (!isEqual(newAddedTags, addedTags)) {
      setAddedTags(newAddedTags);
    }
    if (!isEqual(newRemovedTags, removedTags)) {
      setRemovedTags(newRemovedTags);
    }
  };

  return (
    <ErrorHandler location="src/features/EidSearch/AssignTags/AssignTags.jsx">
      <Modal
        isOpen={isOpen}
        onClose={() => {
          return setIsOpen(false);
        }}
        title="Assign Tags"
      >
        <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
          <Modal.Section>
            <Grid>
              {isLoading || isFetching || isUndefined(tags) ? (
                <Grid.Col
                  span={{
                    xs: '100%',
                  }}
                >
                  <Loader height="100%" width="100%" />
                </Grid.Col>
              ) : (
                <Grid.Col
                  span={{
                    xs: '50%',
                  }}
                >
                  <TagSelectionField
                    form={form}
                    handleChange={handleChange}
                    isRequired={false}
                    systemDefinedTags={false}
                  />
                </Grid.Col>
              )}
            </Grid>
          </Modal.Section>
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button isDisabled={!form?.formState?.isDirty} type="submit" variant="solid">
                Assign
              </Button>
            </Layout.Group>
          </Modal.Section>
        </FormProvider>
      </Modal>
    </ErrorHandler>
  );
}
